import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAutocomplete from "components/MDAutocomplete";
import api from "services/api";

const emptySyarat = {
  nama_syarat: "",
  jenis_input: "FILE",
  wajib: true,
  ekstensi_file: ".pdf,.jpg,.jpeg,.png",
  urutan: 1,
  status: "AKTIF",
};

const JENIS_INPUT_OPTIONS = ["FILE", "TEXT", "NUMBER", "DATE", "COORDINATE"];
const STATUS_OPTIONS = ["AKTIF", "NONAKTIF"];

function JenisIzinDetailDialog({ open, item, onClose, onEditJenis }) {
  const [detail, setDetail] = useState(null);
  const [syarat, setSyarat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syaratFormOpen, setSyaratFormOpen] = useState(false);
  const [syaratMode, setSyaratMode] = useState("create");
  const [selectedSyarat, setSelectedSyarat] = useState(null);
  const [syaratForm, setSyaratForm] = useState(emptySyarat);
  const [savingSyarat, setSavingSyarat] = useState(false);

  const loadDetail = () => {
    if (!item?.id) return;
    setLoading(true);
    Promise.all([api.get(`jenis-izin/${item.id}`), api.get(`jenis-izin/${item.id}/syarat`)])
      .then(([jenisRes, syaratRes]) => {
        setDetail(jenisRes.data?.data);
        setSyarat(syaratRes.data?.data || []);
      })
      .catch(() => alert("Gagal memuat detail jenis izin"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && item?.id) loadDetail();
  }, [open, item?.id]);

  const openSyaratForm = (mode, row = null) => {
    setSyaratMode(mode);
    setSelectedSyarat(row);
    if (mode === "edit" && row) {
      setSyaratForm({
        nama_syarat: row.nama_syarat || "",
        jenis_input: row.jenis_input || "FILE",
        wajib: !!row.wajib,
        ekstensi_file: row.ekstensi_file || ".pdf,.jpg,.jpeg,.png",
        urutan: row.urutan || 1,
        status: row.status || "AKTIF",
      });
    } else {
      setSyaratForm({ ...emptySyarat, urutan: syarat.length + 1 });
    }
    setSyaratFormOpen(true);
  };

  const handleSaveSyarat = async (e) => {
    e.preventDefault();
    if (!syaratForm.nama_syarat.trim()) {
      alert("Nama persyaratan wajib diisi");
      return;
    }

    const payload = {
      ...syaratForm,
      wajib: syaratForm.wajib ? 1 : 0,
      urutan: Number(syaratForm.urutan) || 1,
    };

    setSavingSyarat(true);
    try {
      if (syaratMode === "edit" && selectedSyarat?.id) {
        await api.put(`syarat-izin/${selectedSyarat.id}`, payload);
      } else {
        await api.post(`jenis-izin/${item.id}/syarat`, payload);
      }
      setSyaratFormOpen(false);
      loadDetail();
    } catch (err) {
      alert(err.response?.data?.message || "Gagal menyimpan persyaratan");
    } finally {
      setSavingSyarat(false);
    }
  };

  const handleDeactivateSyarat = async (row) => {
    if (!window.confirm(`Nonaktifkan persyaratan "${row.nama_syarat}"?`)) return;
    try {
      await api.delete(`syarat-izin/${row.id}`);
      loadDetail();
    } catch (err) {
      alert(err.response?.data?.message || "Gagal menonaktifkan persyaratan");
    }
  };

  if (!item) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ pr: 6 }}>
          Detail Jenis Izin
          <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <MDTypography>Memuat...</MDTypography>
          ) : (
            <>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    {detail?.nama}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Kode: {detail?.kode}
                  </MDTypography>
                </MDBox>
                <MDButton size="small" color="info" variant="outlined" onClick={() => onEditJenis(detail)}>
                  Edit Jenis Izin
                </MDButton>
              </MDBox>

              <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip label={detail?.sektor} size="small" />
                <Chip label={detail?.kategori} size="small" color="info" />
                <Chip label={detail?.status} size="small" color={detail?.status === "AKTIF" ? "success" : "default"} />
                {detail?.perlu_rekomendasi_teknis ? <Chip label="Rekomendasi Teknis" size="small" color="warning" /> : null}
                {detail?.perlu_pemeriksaan_lapangan ? <Chip label="Pemeriksaan Lapangan" size="small" color="error" /> : null}
              </MDBox>

              <MDTypography variant="button" color="text" display="block" mb={3}>
                Output: {detail?.output} • Waktu layanan: {detail?.waktu_layanan}
              </MDTypography>

              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="h6">Persyaratan ({syarat.length})</MDTypography>
                <MDButton size="small" variant="gradient" color="success" onClick={() => openSyaratForm("create")}>
                  + Tambah Persyaratan
                </MDButton>
              </MDBox>

              {syarat.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  Belum ada persyaratan.
                </MDTypography>
              ) : (
                syarat.map((s, idx) => (
                  <MDBox
                    key={s.id}
                    p={2}
                    mb={1}
                    borderRadius="lg"
                    bgColor="grey-100"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <MDBox>
                      <MDTypography variant="button" fontWeight="medium">
                        {idx + 1}. {s.nama_syarat}
                        {s.wajib ? " *" : ""}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        Jenis: {s.jenis_input} • Urutan: {s.urutan} • Status: {s.status}
                      </MDTypography>
                      {s.jenis_input === "FILE" && s.ekstensi_file ? (
                        <MDTypography variant="caption" color="text" display="block">
                          Ekstensi: {s.ekstensi_file}
                        </MDTypography>
                      ) : null}
                    </MDBox>
                    <MDBox display="flex" gap={0.5} flexShrink={0}>
                      <MDButton size="small" color="info" variant="text" onClick={() => openSyaratForm("edit", s)}>
                        Edit
                      </MDButton>
                      {s.status === "AKTIF" ? (
                        <MDButton size="small" color="error" variant="text" onClick={() => handleDeactivateSyarat(s)}>
                          Nonaktifkan
                        </MDButton>
                      ) : null}
                    </MDBox>
                  </MDBox>
                ))
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={onClose}>
            Tutup
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={syaratFormOpen} onClose={() => setSyaratFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{syaratMode === "edit" ? "Edit Persyaratan" : "Tambah Persyaratan"}</DialogTitle>
        <DialogContent dividers>
          <MDBox component="form" id="syarat-form" onSubmit={handleSaveSyarat}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDInput
                  label="Nama Persyaratan *"
                  fullWidth
                  value={syaratForm.nama_syarat}
                  onChange={(e) => setSyaratForm((p) => ({ ...p, nama_syarat: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDAutocomplete
                  label="Jenis Input"
                  fullWidth
                  options={JENIS_INPUT_OPTIONS}
                  value={syaratForm.jenis_input}
                  onChange={(value) => setSyaratForm((p) => ({ ...p, jenis_input: value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDInput
                  label="Urutan"
                  type="number"
                  fullWidth
                  value={syaratForm.urutan}
                  onChange={(e) => setSyaratForm((p) => ({ ...p, urutan: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label="Ekstensi File"
                  fullWidth
                  value={syaratForm.ekstensi_file}
                  onChange={(e) => setSyaratForm((p) => ({ ...p, ekstensi_file: e.target.value }))}
                  disabled={syaratForm.jenis_input !== "FILE"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDAutocomplete
                  label="Status"
                  fullWidth
                  options={STATUS_OPTIONS}
                  value={syaratForm.status}
                  onChange={(value) => setSyaratForm((p) => ({ ...p, status: value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDBox display="flex" alignItems="center" pt={2}>
                  <Switch
                    checked={syaratForm.wajib}
                    onChange={(e) => setSyaratForm((p) => ({ ...p, wajib: e.target.checked }))}
                  />
                  <MDTypography variant="button">Persyaratan Wajib</MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setSyaratFormOpen(false)}>
            Batal
          </MDButton>
          <MDButton variant="gradient" color="info" type="submit" form="syarat-form" disabled={savingSyarat}>
            {savingSyarat ? "Menyimpan..." : "Simpan Persyaratan"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

JenisIzinDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEditJenis: PropTypes.func.isRequired,
};

export default JenisIzinDetailDialog;
